/** Voce narrativa 6AMICO — server OpenAI + fallback browser (femminile, naturale). */

/** Voce TTS server: femminile, calda, adatta all'italiano narrativo. */
export const TTS_SERVER_VOICE = "nova";

export const TTS_SERVER_SPEED = 0.93;
export const TTS_BROWSER_RATE = 0.93;
export const TTS_BROWSER_PITCH = 1.02;

/** BCP-47 per Web Speech / allineamento bandierina. */
export const SPEECH_LANG = {
  it: "it-IT",
  en: "en-US",
  fr: "fr-FR",
  de: "de-DE",
  es: "es-ES",
  pt: "pt-PT",
  hr: "hr-HR",
};

export function speechCodeForLang(lang) {
  const raw = String(lang || "it").trim();
  if (/^[a-z]{2}-[A-Za-z]{2}$/i.test(raw)) {
    return `${raw.slice(0, 2).toLowerCase()}-${raw.slice(3).toUpperCase()}`;
  }
  const base = raw.split(/[-_]/)[0].toLowerCase();
  return SPEECH_LANG[base] || SPEECH_LANG.it;
}

export function ttsRequestBody(text) {
  return {
    text,
    voice: TTS_SERVER_VOICE,
    speed: TTS_SERVER_SPEED,
  };
}

const FEMALE_VOICE_HINTS = [
  "female",
  "woman",
  "donna",
  "elsa",
  "alice",
  "lucia",
  "paola",
  "silvia",
  "federica",
  "elisa",
  "giulia",
  "samantha",
  "zira",
  "helena",
  "heloisa",
  "maria",
  "vittoria",
  "google italiano",
  "microsoft elsa",
  "nova",
  "shimmer",
  "coral",
];

function normalizeLang(code = "it-IT") {
  return String(code).replace("_", "-").toLowerCase();
}

export function getSpeechVoices() {
  if (typeof window === "undefined" || !window.speechSynthesis) return [];
  return window.speechSynthesis.getVoices() || [];
}

let speechVoicesPrimed = false;

/**
 * Pre-carica il catalogo voci internazionali (IT, EN, FR…).
 * Chrome/Safari espongono le voci solo dopo voiceschanged.
 */
export function primeSpeechVoicesCatalog() {
  if (typeof window === "undefined" || !window.speechSynthesis || speechVoicesPrimed) {
    return;
  }
  speechVoicesPrimed = true;
  const synth = window.speechSynthesis;
  // Warm immediato se già disponibili
  synth.getVoices();
  // Tiene pronte le voci internazionali al caricamento asincrono del browser
  synth.onvoiceschanged = () => {
    synth.getVoices();
  };
}

/** Attende il caricamento voci (Chrome/Safari le espone in modo asincrono). */
export function waitForSpeechVoices(timeoutMs = 900) {
  primeSpeechVoicesCatalog();
  return new Promise((resolve) => {
    const existing = getSpeechVoices();
    if (existing.length) {
      resolve(existing);
      return;
    }
    if (typeof window === "undefined" || !window.speechSynthesis) {
      resolve([]);
      return;
    }
    const finish = () => {
      window.speechSynthesis.removeEventListener("voiceschanged", onChange);
      clearTimeout(timer);
      resolve(getSpeechVoices());
    };
    const onChange = () => finish();
    window.speechSynthesis.addEventListener("voiceschanged", onChange);
    const timer = window.setTimeout(finish, timeoutMs);
  });
}

function scoreVoice(voice, langBase, langTagNorm) {
  const name = voice.name.toLowerCase();
  const lang = normalizeLang(voice.lang);
  let score = 0;
  if (langTagNorm && lang.startsWith(langTagNorm)) score += 25;
  else if (lang.startsWith(langBase)) score += 10;
  if (FEMALE_VOICE_HINTS.some((hint) => name.includes(hint))) score += 20;
  if (name.includes("premium") || name.includes("enhanced") || name.includes("natural")) score += 5;
  if (langBase === "it" && (name.includes("italian") || name.includes("italiano"))) score += 8;
  return score;
}

/**
 * Voce nativa per il tag BCP-47 corrente (find sull'array getVoices).
 * Match: tag completo (en-us) → base lingua (en) → null.
 */
export function pickPreferredSpeechVoice(langCode = "it-IT", voices = getSpeechVoices()) {
  const langTag = speechCodeForLang(langCode);
  const langTagNorm = normalizeLang(langTag);
  const langBase = langTagNorm.split("-")[0];
  if (!voices.length) return null;

  const list = voices.length ? voices : getSpeechVoices();

  // 1) Tag BCP-47 esatto / prefisso (es. en-US)
  const byTag = list.find((voice) => normalizeLang(voice.lang).startsWith(langTagNorm));
  if (byTag) {
    const femaleSame = list.find(
      (voice) =>
        normalizeLang(voice.lang).startsWith(langTagNorm) &&
        FEMALE_VOICE_HINTS.some((h) => voice.name.toLowerCase().includes(h)),
    );
    return femaleSame || byTag;
  }

  // 2) Solo codice lingua (es. en)
  const byBase = list.find((voice) => normalizeLang(voice.lang).startsWith(langBase));
  if (byBase) {
    const femaleBase = list.find(
      (voice) =>
        normalizeLang(voice.lang).startsWith(langBase) &&
        FEMALE_VOICE_HINTS.some((h) => voice.name.toLowerCase().includes(h)),
    );
    return femaleBase || byBase;
  }

  const ranked = list
    .map((voice) => ({ voice, score: scoreVoice(voice, langBase, langTagNorm) }))
    .filter((entry) => entry.score > 0)
    .sort((a, b) => b.score - a.score);

  return ranked[0]?.voice || null;
}

/** Applica voce, ritmo e tono all'utterance del browser. */
export async function configureBrowserUtterance(utterance, langCode = "it-IT") {
  const langTag = speechCodeForLang(langCode);
  const voices = await waitForSpeechVoices();
  const preferred = pickPreferredSpeechVoice(langTag, voices);
  utterance.lang = langTag;
  utterance.rate = TTS_BROWSER_RATE;
  utterance.pitch = TTS_BROWSER_PITCH;
  if (preferred) utterance.voice = preferred;
  return utterance;
}

function isBenignSpeechError(err) {
  const name = String(err?.error || err?.message || err || "").toLowerCase();
  return name.includes("interrupted") || name.includes("canceled") || name.includes("cancelled");
}

/**
 * Pulisce markdown / simboli prima della lettura vocale
 * (* **, |, #, _) così la voce non li pronuncia.
 * Idempotente: IT ed EN passano dallo stesso cleaner via riproduciTesto.
 */
export function pulisciTestoPerLettura(testoGreggio) {
  let testoPulito = String(testoGreggio || "");

  // 1. Asterischi (singoli e doppi — junk bold/italic markdown)
  testoPulito = testoPulito.replace(/\*/g, "");

  // 2. Barre verticali → virgole (pause naturali bilingue)
  testoPulito = testoPulito.replace(/\|/g, ",");

  // 3. Simboli markdown / speciali (#, _, backtick)
  testoPulito = testoPulito.replace(/[#_`]/g, "");

  // 4. Virgole ripetute / spazi attorno alle virgole dopo la sostituzione |
  testoPulito = testoPulito.replace(/,(?:\s*,)+/g, ",");
  testoPulito = testoPulito.replace(/\s*,\s*/g, ", ");

  // 5. Spazi multipli
  testoPulito = testoPulito.replace(/\s+/g, " ").trim();

  return testoPulito;
}

/**
 * Avvia la lettura vocale del testo grezzo (con pulizia markdown).
 * Alias esplicito usato da Ascolta / TTS browser.
 */
export function avviaLetturaVocale(testoGreggio, linguaSelezionata, opts = {}) {
  return riproduciTesto(testoGreggio, linguaSelezionata, opts);
}

/**
 * Sintesi vocale: testo tradotto + lingua UI corrente (BCP-47) + voce nativa.
 * cancel() all'inizio evita code congelate dopo il cambio bandierina.
 *
 * @param {string} testoDaLeggere — testo già nella lingua del sito (anche con markdown)
 * @param {string} linguaSelezionata — es. 'it-IT', 'en-US' oppure 'it' / 'en'
 * @param {{ onEnd?: () => void, onError?: (err: Error) => void }} [opts]
 * @returns {SpeechSynthesisUtterance | null}
 */
export function riproduciTesto(testoDaLeggere, linguaSelezionata, { onEnd, onError } = {}) {
  if (typeof window === "undefined" || !window.speechSynthesis || !window.SpeechSynthesisUtterance) {
    onError?.(new Error("speech_unavailable"));
    return null;
  }

  // Pulizia testo prima della sintesi (*, **, |, #, _)
  const testo = pulisciTestoPerLettura(testoDaLeggere);
  if (!testo) {
    onError?.(new Error("empty_text"));
    return null;
  }

  const synth = window.speechSynthesis;

  // Reset coda / stato bloccato (soprattutto dopo cambio lingua)
  try {
    synth.cancel();
  } catch {
    /* ignore */
  }
  try {
    if (synth.paused) synth.resume();
  } catch {
    /* ignore */
  }

  const langTag = speechCodeForLang(linguaSelezionata);
  const utterance = new SpeechSynthesisUtterance(testo);
  utterance.lang = langTag;
  utterance.rate = TTS_BROWSER_RATE;
  utterance.pitch = TTS_BROWSER_PITCH;

  const voices = synth.getVoices() || [];
  const langNorm = normalizeLang(langTag);
  const voceAdatta =
    voices.find((voice) => normalizeLang(voice.lang).startsWith(langNorm)) ||
    voices.find((voice) => normalizeLang(voice.lang).startsWith(langNorm.split("-")[0])) ||
    pickPreferredSpeechVoice(langTag, voices);
  if (voceAdatta) {
    utterance.voice = voceAdatta;
  }

  if (onEnd) utterance.onend = () => onEnd();
  if (onError) {
    utterance.onerror = (event) => {
      if (isBenignSpeechError(event)) return;
      onError(new Error("speech_error"));
    };
  }

  window.setTimeout(() => {
    try {
      if (synth.paused) synth.resume();
      synth.speak(utterance);
    } catch (err) {
      onError?.(err instanceof Error ? err : new Error("speech_error"));
    }
  }, 40);

  return utterance;
}

/** Come riproduciTesto / avviaLetturaVocale, ma attende il catalogo voci. */
export async function riproduciTestoAsync(testoDaLeggere, linguaSelezionata, opts = {}) {
  await waitForSpeechVoices();
  return riproduciTesto(testoDaLeggere, linguaSelezionata, opts);
}

export async function avviaLetturaVocaleAsync(testoGreggio, linguaSelezionata, opts = {}) {
  return riproduciTestoAsync(testoGreggio, linguaSelezionata, opts);
}

// Avvio: registra onvoiceschanged per tenere pronte le voci internazionali
primeSpeechVoicesCatalog();
